import React, { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlayerPlay, IconX } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';

import { GET_HISTORICAL_OVERRIDE_SCHEDULED_IMPORTS } from '../../apollo/queries/getHistoricalOverrideScheduledImports';
import { CANCEL_SCHEDULED_HISTORICAL_IMPORT } from '../../apollo/mutations/cancelScheduledHistoricalImport';
import { FORCE_RUN_HISTORICAL_IMPORT } from '../../apollo/mutations/forceRunHistoricalImport';
import { ImportType } from '../../types/import';
import { useAuth } from '../../context/AuthProvider';
import { checkUserRole } from '../../utility/userRoles';
import { UserRole } from '../../types/users';

interface ScheduledImportRow {
    id: string;
    fileName: string;
    status: string;
    createdAt: string;
    scheduledFor: string | null;
    createdBy?: {
        firstName: string;
        lastName: string;
    } | null;
}

interface Props {
    readonly onChanged?: () => void;
}

export function HistoricalOverrideScheduledTable({
    onChanged,
}: Props): JSX.Element {
    const { authUser } = useAuth();
    const isSuperAdmin = checkUserRole(authUser, [UserRole.SUPER_ADMIN]);

    const { data, refetch, loading } = useQuery(
        GET_HISTORICAL_OVERRIDE_SCHEDULED_IMPORTS,
        {
            variables: {
                input: {
                    limit: 50,
                    offset: 0,
                    importType: ImportType.DailySalesHistoricalManualOverride,
                    statuses: ['Scheduled', 'Queued'],
                },
            },
            pollInterval: 5000,
            fetchPolicy: 'cache-and-network',
        },
    );

    const [cancelMutation] = useMutation(CANCEL_SCHEDULED_HISTORICAL_IMPORT);
    const [forceRunMutation, { loading: forceRunLoading }] = useMutation(
        FORCE_RUN_HISTORICAL_IMPORT,
    );

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                refetch().catch(() => undefined);
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [refetch]);

    const rows: ScheduledImportRow[] = data?.getManualImportLogs?.logs ?? [];

    const handleForceRun = (row: ScheduledImportRow) => {
        modals.openConfirmModal({
            title: 'Run scheduled import now?',
            centered: true,
            children: (
                <Text size="sm">
                    &quot;{row.fileName}&quot; will be dispatched to the worker
                    immediately, bypassing its scheduled time. This is intended
                    for testing and ad-hoc reruns.
                </Text>
            ),
            labels: { confirm: 'Run now', cancel: 'Keep scheduled' },
            confirmProps: { color: 'blue', size: 'xs' },
            cancelProps: { size: 'xs' },
            groupProps: {
                gap: 'xs',
                justify: 'flex-end',
                wrap: 'nowrap',
            },
            onConfirm: async () => {
                try {
                    await forceRunMutation({ variables: { id: row.id } });
                    notifications.show({
                        title: 'Import dispatched',
                        message: `"${row.fileName}" has been queued to the worker.`,
                        color: 'green',
                    });
                    await refetch();
                    onChanged?.();
                } catch (e) {
                    notifications.show({
                        title: 'Run now failed',
                        message:
                            e instanceof Error
                                ? e.message
                                : 'Unknown error while dispatching import',
                        color: 'red',
                    });
                }
            },
        });
    };

    const handleCancel = (row: ScheduledImportRow) => {
        const queued = row.status === 'Queued';
        modals.openConfirmModal({
            title: 'Cancel scheduled historical import?',
            centered: true,
            children: (
                <Text size="sm">
                    {queued
                        ? 'This file has already been queued to the worker. We will attempt to terminate the job, but if it has already started this cancellation is best-effort and the import may still complete.'
                        : `"${row.fileName}" will be cancelled and the uploaded file deleted. This action cannot be undone.`}
                </Text>
            ),
            labels: { confirm: 'Cancel import', cancel: 'Keep scheduled' },
            confirmProps: { color: 'red', size: 'xs' },
            cancelProps: { size: 'xs' },
            groupProps: {
                gap: 'xs',
                justify: 'flex-end',
                wrap: 'nowrap',
            },
            onConfirm: async () => {
                try {
                    await cancelMutation({ variables: { id: row.id } });
                    notifications.show({
                        title: 'Import cancelled',
                        message: `"${row.fileName}" has been cancelled.`,
                        color: 'green',
                    });
                    await refetch();
                    onChanged?.();
                } catch (e) {
                    notifications.show({
                        title: 'Cancel failed',
                        message:
                            e instanceof Error
                                ? e.message
                                : 'Unknown error while cancelling import',
                        color: 'red',
                    });
                }
            },
        });
    };

    return (
        <DataTable<ScheduledImportRow>
            withTableBorder
            striped
            verticalSpacing="xs"
            fz="sm"
            minHeight={120}
            noRecordsText="No imports scheduled."
            records={rows}
            idAccessor="id"
            fetching={loading && rows.length === 0}
            columns={[
                {
                    accessor: 'fileName',
                    title: 'File',
                    render: row => (
                        <Text size="sm" fw={500}>
                            {row.fileName}
                        </Text>
                    ),
                },
                {
                    accessor: 'scheduledFor',
                    title: 'Scheduled for',
                    render: row =>
                        row.scheduledFor
                            ? dayjs(row.scheduledFor).format(
                                  'DD MMM YYYY HH:mm',
                              )
                            : '—',
                },
                {
                    accessor: 'createdBy',
                    title: 'Uploaded by',
                    render: row =>
                        row.createdBy
                            ? `${row.createdBy.firstName} ${row.createdBy.lastName}`
                            : '—',
                },
                {
                    accessor: 'createdAt',
                    title: 'Uploaded at',
                    render: row =>
                        dayjs(row.createdAt).format('DD MMM YYYY HH:mm'),
                },
                {
                    accessor: 'status',
                    title: 'Status',
                    render: row => (
                        <Text
                            size="sm"
                            c={row.status === 'Queued' ? 'blue' : 'dimmed'}
                        >
                            {row.status}
                        </Text>
                    ),
                },
                {
                    accessor: 'actions',
                    title: '',
                    textAlign: 'right',
                    render: row => (
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                            {isSuperAdmin && row.status === 'Scheduled' && (
                                <Tooltip label="Run now (bypass schedule)">
                                    <ActionIcon
                                        color="blue"
                                        variant="subtle"
                                        loading={forceRunLoading}
                                        onClick={() => handleForceRun(row)}
                                    >
                                        <IconPlayerPlay size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            <Tooltip label="Cancel import">
                                <ActionIcon
                                    color="red"
                                    variant="subtle"
                                    onClick={() => handleCancel(row)}
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    ),
                },
            ]}
        />
    );
}
